<template>
  <div
    class="checkout-wrapper"
    :class="`${sectionThemeClass}-theme`"
    :style="cssVariables"
  >
    <div v-if="content?.title" class="section-title mb-4">
      <h2>{{ content.title || '' }}</h2>
    </div>

    <div v-if="content?.description" class="text-content">
      <!-- eslint-disable vue/no-v-html -->
      <div v-html="content.description || ''" />
    </div>

    <Loading
      v-if="isLoading || isFetchingForm || isUpdating"
      class="loading-wrapper"
      placement="internal"
    />

    <div
      class="checkout__container"
      :class="[`checkout-flow-${orderType}`, isFree && 'checkout-flow-free']"
    >
      <!-- Steps -->
      <CheckoutSteps v-if="activeStep && !isFirstLoad" :steps="checkoutSteps" />

      <div
        v-if="(orderDetails || ticketGuestRegistration) && !isFirstLoad"
        class="checkout__content"
        :class="{ 'checkout__content--no-sidebar': ticketGuestRegistration }"
      >
        <!-- Ticket Purchase Mode: Bulk Ticket Selection -->
        <TicketQuantityPicker
          v-if="activeStep === 'select-tickets-bulk'"
          :tickets="tickets"
          :is-fetching="isFetchingTickets"
          :is-updating="isUpdating"
          @tickets-selected="updateBulkTickets"
        />

        <!-- Step 1 (Registration Mode) -->
        <SelectTicket
          v-if="activeStep === 'select-ticket'"
          :value="mainGuestForm.ticket"
          :tickets="tickets"
          :add-ons="addOns"
          :is-fetching="isFetchingTickets"
          :assign-add-on="assignAddOnToGuest"
          @ticket-selected="assignTicketToGuest"
          @add-on-selected="assignAddOnToGuest"
          @add-on-removed="removeAddOnFromGuest"
        />

        <!-- Step 2 -->
        <UserData
          v-if="activeStep === 'user-data'"
          ref="userDataRef"
          :fields="mainGuestFields"
          :initial-form-data="mainGuestForm.data"
          @update:form-data="(value) => (mainGuestForm.data = value)"
        />

        <!-- Step 3 -->
        <Companions
          v-if="activeStep === 'companions'"
          :fields="companionFields"
          @form-active="(isActive) => (isCompanionsFormActive = isActive)"
          @is-loading="(loading) => (isUpdating = loading)"
          @success="() => fetchOrderData(orderId)"
          @error="(error) => (errorMessage = error)"
          @expired-session="
            () => {
              isUpdating = false
              isExpiredModalShown = true
            }
          "
        />

        <!-- Step 4 -->
        <BillingAddress
          v-if="activeStep === 'billing'"
          :billing-data="mainGuestForm.billing_details"
          :errors="mainGuestForm.billing_errors"
          @update="(value) => (mainGuestForm.billing_details = value)"
        />

        <!-- Step 5 (only when the merchant offers both online payment and bank transfer) -->
        <PaymentStep
          v-if="activeStep === 'payment'"
          v-model="selectedPaymentMethod"
          :online-payment-methods="onlinePaymentMethods"
          :bank-transfer-due-date="bankTransferDueDate"
          :bank-transfer-preferred="bankTransferPreferred"
        />

        <OrderSummary
          v-if="
            !['confirmation'].includes(activeStep) &&
            activeStep &&
            !ticketGuestRegistration
          "
          ref="orderSummaryRef"
          :disable-next="disableNextBtn"
          :bank-transfer-available="bankTransferAvailable"
          @countdown-timer-expired="
            () => {
              isUpdating = false
              isExpiredModalShown = true
            }
          "
          @next="nextStep"
          @back="prevStep"
          @promo-code-changed="fetchOrderData(orderId)"
        />

        <!-- Ticket Guest Registration: simple next button -->
        <div
          v-if="
            ticketGuestRegistration && activeStep === 'user-data' && !isUpdating
          "
          class="checkout__actions mt-6 flex justify-end"
        >
          <button
            type="button"
            class="btn btn-primary"
            :disabled="isUpdating"
            @click="nextStep"
          >
            {{ $t('common.submit') }}
          </button>
        </div>

        <TicketGuestRegistrationConfirmation
          v-if="ticketGuestRegistration && activeStep === 'confirmation'"
          :already-confirmed="ticketGuestAlreadyConfirmed"
        />

        <Confirmation
          v-if="
            !ticketGuestRegistration &&
            activeStep === 'confirmation' &&
            (orderDetails?.order_status === 'completed' ||
              orderDetails?.bank_transfer_instructions)
          "
        />
        <PaymentFailed
          v-if="
            activeStep === 'confirmation' &&
            orderDetails?.order_status === 'payment_failed'
          "
          @new-payment-link-requested="getCheckoutUrl"
        />
      </div>
    </div>

    <Alert
      v-if="isExpiredModalShown"
      show
      :show-alert-icon="false"
      :title="$t('landingpage.checkout-page.expired.title')"
      :message="$t('landingpage.checkout-page.expired.message')"
      :wrapper-class="['expired-checkout-modal', `${sectionThemeClass}-theme`]"
      width="400px"
      body-class="py-8! px-4!"
    >
      <template #buttons>
        <div class="flex items-center justify-center gap-2 pt-2">
          <button
            class="btn btn-primary"
            @click="reloadPageWithoutQueryParameters"
          >
            {{ $t('landingpage.checkout-page.expired.btn-reload.text') }}
          </button>
        </div>
      </template>
    </Alert>

    <Alert
      v-if="errorMessage && !isExpiredModalShown"
      show
      :show-alert-icon="false"
      :title="$t('landingpage.checkout-page.error.title')"
      :message="errorMessage"
      :wrapper-class="['checkout-error-alert', `${sectionThemeClass}-theme`]"
      width="400px"
      body-class="py-8! px-4!"
      :display-cancel-button="false"
      :confirm-text="$t('common.ok')"
      @confirm="errorMessage = ''"
      @close="errorMessage = ''"
    />
  </div>
</template>

<script setup>
import {
  ref,
  provide,
  onMounted,
  onBeforeUnmount,
  computed,
  reactive,
  watch,
} from 'vue'
import Loading from '@/Components/common/Loading.vue'
import CheckoutSteps from '@/Components/LandingPage/Checkout/CheckoutSteps.vue'
import OrderSummary from '@/Components/LandingPage/Checkout/OrderSummary.vue'
import SelectTicket from '@/Components/LandingPage/Checkout/SelectTicket.vue'
import TicketQuantityPicker from '@/Components/LandingPage/Checkout/TicketQuantityPicker.vue'
import { isTicketComingSoon } from '@/LandingPage/Checkout/ticketSalesStatus'
import UserData from '@/Components/LandingPage/Checkout/UserData.vue'
import Companions from '@/Components/LandingPage/Checkout/Companions.vue'
import BillingAddress from '@/Components/LandingPage/Checkout/BillingAddress.vue'
import PaymentStep from '@/Components/LandingPage/Checkout/PaymentStep.vue'
import Confirmation from '@/Components/LandingPage/Checkout/Confirmation.vue'
import TicketGuestRegistrationConfirmation from '@/Components/LandingPage/Checkout/TicketGuestRegistrationConfirmation.vue'
import PaymentFailed from '@/Components/LandingPage/Checkout/PaymentFailed.vue'
import { isCompanionIncomplete } from '@/LandingPage/Checkout/companionCompleteness'
import {
  getJson,
  putJson,
  postJson,
  patchJson,
  useFetch,
  checkoutSessionExpired,
} from '@/LandingPage/Checkout/useCheckoutFetch'
import { useQueryParam } from '@/composables/route'
import { resolveInitialFieldValue } from '@/LandingPage/Checkout/fieldPrefill'
import { dataSet, getNested } from '@landingpage/Common/utils'
import { useI18n } from 'vue-i18n'
import { useFieldPathResolver } from '@landingpage/Registration/composables/useFieldPathResolver'
import { useCheckoutFieldLimitsStore } from '@landingpage/Registration/stores/checkoutFieldLimitsStore'
import { lightOrDark } from '@/composables/color'
import Alert from '@/Components/Alert.vue'

const { t } = useI18n()
const { getFieldPath } = useFieldPathResolver()
const fieldLimitsStore = useCheckoutFieldLimitsStore()

const props = defineProps({
  eventId: { type: String, required: true },
  booking: { type: Object, required: true },
  activeLocale: { type: String, required: true },
  eventSettings: { type: Object, required: true },
  countries: { type: Array, required: true },
  content: { type: Object, default: () => ({}) },
  ticketGuestRegistration: { type: Boolean, default: false },
  ticketGuestAlreadyConfirmed: { type: Boolean, default: false },
  guestCode: { type: String, default: null },
  bankTransferAvailable: { type: Boolean, default: false },
  onlinePaymentMethods: { type: Array, default: () => [] },
  bankTransferDueDate: { type: String, default: null },
  bankTransferPreferred: { type: Boolean, default: false },
  queryParameters: { type: Object, default: () => ({}) },
})

const cssVariables = computed(() => {
  const rootElement = document.documentElement
  const buttonBgColorVar = getComputedStyle(rootElement)
    .getPropertyValue('--button-background-color')
    .trim()
  const bodyBgColorVar = getComputedStyle(rootElement)
    .getPropertyValue('--body-background-color')
    .trim()

  return {
    '--default-button-bg-color':
      buttonBgColorVar === 'transparent' ? bodyBgColorVar : buttonBgColorVar,
  }
})
const mainGuest = computed(() => {
  return orderDetails.value?.booking?.guests.find(
    (guest) => guest.role === 'main',
  )
})
const companionGuests = computed(() => {
  return orderDetails.value?.booking?.guests.filter(
    (guest) => guest.role === 'companion',
  )
})
const hasIncompleteCompanions = computed(() => {
  const companions = companionGuests.value ?? []
  const hasCompanionForm = (companionFields.value?.items?.length ?? 0) > 0
  const allTicketsSoldOut =
    orderType.value !== 'add-ons' &&
    tickets.value.length > 0 &&
    tickets.value.every((t) => t.remaining <= 0 || isTicketComingSoon(t))
  return companions.some((guest) =>
    isCompanionIncomplete(guest, {
      hasCompanionForm,
      orderType: orderType.value,
      allTicketsSoldOut,
    }),
  )
})
const requiredBillingFields = [
  'billing_first_name',
  'billing_last_name',
  'billing_email',
  'billing_address',
  'billing_city',
  'billing_zip',
  'billing_country',
]

const billingDetailsComplete = computed(() =>
  requiredBillingFields.every((key) => {
    const value = mainGuestForm.value.billing_details?.[key]
    return value !== undefined && value !== null && value !== ''
  }),
)

const disableNextBtn = computed(() => {
  let updatingState =
    isCompanionsFormActive.value ||
    isLoading.value ||
    isUpdating.value ||
    (activeStep.value === 'select-ticket' &&
      orderType.value !== 'add-ons' &&
      mainGuestForm.value.ticket === undefined) ||
    (activeStep.value === 'select-tickets-bulk' &&
      !orderDetails.value?.line_items?.length) ||
    (activeStep.value === 'companions' && hasIncompleteCompanions.value) ||
    (activeStep.value === 'billing' && !billingDetailsComplete.value) ||
    (activeStep.value === 'payment' && !selectedPaymentMethod.value)
  return updatingState
})
const orderType = computed(() => {
  if (!tickets.value.length && addOns.value.length) return 'add-ons'

  return 'all' // all, add-ons
})
const isFree = computed(() => {
  if (
    !orderDetails.value?.line_items?.length ||
    (orderDetails.value?.line_items?.length &&
      orderDetails.value?.total_gross_price === 0)
  )
    return true

  return false
})
const isTicketPurchaseMode = computed(() => {
  return orderDetails.value?.checkout_mode === 'ticket_purchase'
})
const checkoutSteps = computed(() => {
  const steps = []
  // The payment step (choose online vs bank transfer) only appears when the merchant offers both.
  const paymentStepAvailable =
    props.bankTransferAvailable && props.onlinePaymentMethods.length > 0

  // Ticket Guest Registration Mode (guest filling in personal data)
  if (props.ticketGuestRegistration) {
    if (props.ticketGuestAlreadyConfirmed) return ['confirmation']
    return ['user-data', 'confirmation']
  }

  // Ticket Purchase Mode has simplified flow
  if (isTicketPurchaseMode.value) {
    if (tickets.value.length) steps.push('select-tickets-bulk')
    if (!isFree.value) {
      steps.push('billing')
      if (paymentStepAvailable) steps.push('payment')
    }
    steps.push('confirmation')
    return steps
  }

  // Registration Mode (original flow)
  if (orderType.value === 'add-ons') {
    steps.push('user-data')
    if (addOns.value.length) steps.push('select-ticket')
  } else {
    if (tickets.value.length || addOns.value.length) steps.push('select-ticket')
    steps.push('user-data')
  }
  if (isCompanionsEnabled.value) {
    steps.push('companions')
  }
  if (!isFree.value) {
    steps.push('billing')
    if (paymentStepAvailable) steps.push('payment')
  }
  steps.push('confirmation')

  return steps
})

const sectionThemeClass = computed(() => {
  if (!sectionBgColor.value) return ''
  return lightOrDark(sectionBgColor.value)
})

const route = window.route
const query = reactive(useQueryParam())
const isFirstLoad = ref(true)
const isLoading = ref(true)
const isFetchingTickets = ref(true)
const isFetchingAddons = ref(true)
const isFetchingForm = ref(false)
const isUpdating = ref(false)
const isCompanionsFormActive = ref(false)
const activeStep = ref('')
const activeStepIndex = ref(0)
const errorMessage = ref('')
const ticketsHasPrice = ref(false)
const addOnsHasPrice = ref(false)
const isCompanionsEnabled = computed(() => {
  if (!props.eventSettings?.allow_companions) return false
  const main = mainGuest.value
  if (!main) return true
  if (main.max_companion_limit === 0) return false
  return true
})
const sectionBgColor = ref('')
const isExpiredModalShown = ref(false)
const orderSummaryRef = ref(null)
const lineItemsEmptyOnFirstLoad = ref(false)
const pollingIntervalId = ref(null)

const orderId = ref(null)
const orderDetails = ref(null)
const tickets = ref([])
const addOns = ref([])
const mainGuestFields = ref({})
const companionFields = ref({})
const paymentUrl = ref(null)
const selectedPaymentMethod = ref(null)

watch(
  () => activeStep.value,
  (step) => {
    if (
      step === 'payment' &&
      props.bankTransferPreferred &&
      selectedPaymentMethod.value === null
    ) {
      selectedPaymentMethod.value = 'bank_transfer'
    }
  },
)

const mainGuestForm = ref({
  ticket: null,
  data: {},
  billing_details: {},
  billing_errors: {},
})

const userDataRef = ref(null)

onMounted(async () => {
  const rootElement = document.documentElement
  const sectionBgColorVar = getComputedStyle(rootElement)
    .getPropertyValue('--section-background-color')
    .trim()
  sectionBgColor.value = sectionBgColorVar

  // Ticket Guest Registration Mode
  if (props.ticketGuestRegistration) {
    if (props.ticketGuestAlreadyConfirmed) {
      activeStep.value = 'confirmation'
      isLoading.value = false
      isFirstLoad.value = false
      return
    }

    await fetchTicketGuestRegistrationForms()
    await fetchTicketGuestRegistrationData()
    activeStep.value = 'user-data'
    isLoading.value = false
    isFirstLoad.value = false
    return
  }

  if (query?.result === 'success' && query?.order_id) {
    activeStep.value = 'confirmation'
    orderId.value = query.order_id
    await fetchOrderData(query.order_id)
    // Bank-transfer ("pay by invoice") orders stay payment_pending until the funds
    // arrive (days later), so polling would only flash the loading spinner over the
    // IBAN instructions; poll only for the card async-payment case that resolves quickly.
    if (
      orderDetails.value?.order_status === 'payment_pending' &&
      !orderDetails.value?.bank_transfer_instructions
    ) {
      pollingIntervalId.value = setInterval(() => {
        fetchOrderData(query.order_id)
      }, 5000)
    }
    return
  }

  await createOrder()
  if (!orderId.value) return
  await fetchFieldAvailabilities()
  if (!isTicketPurchaseMode.value) {
    await fetchFormDefinition()
  }
  await fetchOrderData(orderId.value)

  lineItemsEmptyOnFirstLoad.value = orderDetails.value?.line_items?.length === 0
})

onBeforeUnmount(() => {
  if (pollingIntervalId.value) {
    clearInterval(pollingIntervalId.value)
  }
})

const prevStep = () => {
  activeStepIndex.value -= 1
  activeStep.value = checkoutSteps.value[activeStepIndex.value]

  if (activeStep.value !== 'companions') {
    isCompanionsFormActive.value = false
  }

  errorMessage.value = ''
}
const nextStep = async () => {
  // Ticket Guest Registration: submit personal data and confirm
  if (props.ticketGuestRegistration && activeStep.value === 'user-data') {
    const saved = await submitTicketGuestRegistration()
    if (!saved) return

    activeStepIndex.value += 1
    activeStep.value = 'confirmation'
    return
  }

  if (activeStep.value === 'user-data') {
    const saved = await updateMainGuest(mainGuest.value)
    if (!saved) return
  }

  if (activeStep.value === 'billing') {
    // When the payment step follows, persist billing then advance to it; otherwise place the order
    // directly via the hosted card checkout (single-method flow, unchanged).
    if (checkoutSteps.value[activeStepIndex.value + 1] === 'payment') {
      if (await persistBillingDetails()) {
        activeStepIndex.value += 1
        activeStep.value = 'payment'
      }
      return
    }

    updateBilling()
    return
  }

  if (activeStep.value === 'payment') {
    if (selectedPaymentMethod.value === 'bank_transfer') {
      payByBankTransfer()
    } else {
      getCheckoutUrl()
    }
    return
  }

  if (checkoutSteps.value[activeStepIndex.value + 1] === 'confirmation') {
    getCheckoutUrl()
    return
  }

  activeStepIndex.value += 1
  activeStep.value = checkoutSteps.value[activeStepIndex.value]
}

const generateGuestFields = (guest, fields) => {
  let initialData = {
    contact: {},
    extended_fields: {},
    booking: { extended_fields: {} },
  }

  if (fields) {
    fields.forEach((field) => {
      const path = getFieldPath(field)
      if (path) {
        const value = resolveInitialFieldValue(
          field,
          getNested(guest, path),
          props.queryParameters,
        )
        dataSet(initialData, path, value)
      }
    })
  }

  if (guest?.guest_group_id) {
    initialData.guest_group_id = guest.guest_group_id
  }

  return initialData
}

const getBillingDetails = () => {
  const billingKeys = [
    'billing_first_name',
    'billing_last_name',
    'billing_email',
    'billing_company_name',
    'billing_address',
    'billing_city',
    'billing_zip',
    'billing_country',
    'billing_phone',
    'billing_vat_number',
  ]

  return Object.fromEntries(
    billingKeys.map((key) => [key, orderDetails.value?.[key] ?? null]),
  )
}

// Drop the loading state so whatever is rendered underneath (an alert, the expired dialog) becomes
// visible; isFirstLoad has to go with it, since it gates the panel's own markup.
const stopLoading = () => {
  isLoading.value = false
  isFirstLoad.value = false
}

// A 401/419 from any checkout call means the session behind this page is gone (expired, logged
// out, or a browser that blocks the cookie, e.g. in a cross-site iframe); only a reload recovers.
watch(checkoutSessionExpired, (expired) => {
  if (expired) {
    stopLoading()
    isExpiredModalShown.value = true
  }
})

const createOrder = async () => {
  isLoading.value = true
  const { data, error, statusCode } = await postJson(
    route('api.checkout.order.create', { event: props.eventId }),
    {
      booking_id: props.booking ? props.booking?.data?.booking?.id : null,
    },
  )
  // Every failure here has to clear the spinner itself: onMounted bails out right after this call,
  // so isLoading would otherwise stay true forever and the panel would show nothing but "loading".
  // A 404/422 means the booking this browser still points at is gone (its abandoned
  // checkout was reaped), which the expired dialog's reload recovers from — the server issues a
  // fresh booking on the next render. Anything else is a plain error (AIRLST-5461).
  if (statusCode.value === 404 || statusCode.value === 422) {
    stopLoading()
    isExpiredModalShown.value = true
    return
  }
  if (error.value) {
    stopLoading()
    errorMessage.value = t('landingpage.checkout-page.error.generic')
    return
  }
  orderId.value = data.value.data?.id
  // A settled order handed back here means the registration is already finished (AIRLST-5662):
  // paid, or awaiting a bank transfer with the invoice issued. Show its confirmation instead of
  // treating it as a cart — the ticket step would only produce 422s against a non-cart order.
  // Mirrors ResolveCheckoutOrderAction exactly: an invoice number alone is not enough, the
  // order must still be payment_pending.
  if (
    query?.result !== 'success' &&
    (data.value.data?.order_status === 'completed' ||
      (data.value.data?.order_status === 'payment_pending' &&
        data.value.data?.invoice_number))
  ) {
    const redirectUrl = new URL(window.location.href)
    redirectUrl.searchParams.set('result', 'success')
    redirectUrl.searchParams.set('order_id', orderId.value)
    paymentUrl.value = redirectUrl.toString()
    redirectToPayment()
  }
}

// Fetch Order details
const fetchOrderData = async (orderId) => {
  isLoading.value = true
  errorMessage.value = ''
  const { data, error, statusCode } = await getJson(
    route('api.checkout.order.show', {
      event: props.eventId,
      order: orderId,
    }),
  )
  if (statusCode.value && statusCode.value === 404) {
    if (pollingIntervalId.value) {
      clearInterval(pollingIntervalId.value)
    }
    isExpiredModalShown.value = true
    return
  }
  if (error.value) return
  orderDetails.value = data.value.data

  if (
    lineItemsEmptyOnFirstLoad.value &&
    orderDetails.value?.line_items?.length &&
    orderSummaryRef.value
  ) {
    orderSummaryRef.value.startCountdown()
  }
  // Stop polling if order is completed
  if (
    orderDetails.value?.order_status === 'completed' &&
    pollingIntervalId.value
  ) {
    clearInterval(pollingIntervalId.value)
    pollingIntervalId.value = null
  }

  // Fetch form definitions for ticket purchase mode on confirmation
  if (
    orderDetails.value?.order_status === 'completed' &&
    isTicketPurchaseMode.value &&
    !mainGuestFields.value?.items
  ) {
    await fetchFormDefinition()
  }

  if (activeStep.value !== 'confirmation') {
    mainGuestForm.value.billing_details = getBillingDetails()
    mainGuestForm.value.data = generateGuestFields(
      mainGuest.value,
      mainGuestFields.value.items,
    )
  }

  if (
    activeStep.value !== 'companions' &&
    activeStep.value !== 'confirmation'
  ) {
    if (isTicketPurchaseMode.value) {
      await fetchOrderTickets()
    } else {
      await fetchGuestTickets(mainGuest.value)
      await fetchGuestAddons(mainGuest.value)
    }
  }

  if (!activeStep.value) {
    activeStep.value = checkoutSteps.value[0]
  }

  if (activeStep.value === 'confirmation') {
    activeStepIndex.value = checkoutSteps.value?.length - 1
  }

  // Keep loading state until order is completed when on confirmation step.
  // Bank-transfer ("pay by invoice") orders stay payment_pending by design and instead
  // render the Confirmation step with the IBAN instructions, so they must not be held on
  // the loading spinner.
  if (
    activeStep.value === 'confirmation' &&
    orderDetails.value?.order_status !== 'completed' &&
    orderDetails.value?.order_status !== 'payment_failed' &&
    !orderDetails.value?.bank_transfer_instructions
  ) {
    isLoading.value = true
  } else {
    isLoading.value = false
  }

  isFirstLoad.value = false
  recalculateFieldLimits()
}

// Fetch available tickets at the order level (for ticket purchase mode)
const fetchOrderTickets = async () => {
  const { data, statusCode } = await getJson(
    route('api.checkout.tickets.index', {
      event: props.eventId,
      order: orderId.value,
    }),
  )
  if (statusCode.value && statusCode.value === 404) {
    isExpiredModalShown.value = true
    return
  }
  tickets.value = data.value.data
  ticketsHasPrice.value = tickets.value.some((t) => (t.price || 0) > 0)
  isFetchingTickets.value = false
}

// Fetch Main Guest Tickets
const fetchGuestTickets = async (guest) => {
  const { data, statusCode } = await getJson(
    route('api.checkout.guest.tickets.index', {
      event: props.eventId,
      order: orderId.value,
      guest: guest?.id,
    }),
  )
  if (statusCode.value && statusCode.value === 404) {
    isExpiredModalShown.value = true
    return
  }
  tickets.value = data.value.data
  mainGuestForm.value.ticket = tickets.value?.find(
    ({ guest_has_ticket }) => guest_has_ticket,
  )?.id
  ticketsHasPrice.value = tickets.value.some((t) => (t.price || 0) > 0)
  isFetchingTickets.value = false
}

// Update bulk tickets (Ticket Purchase Mode)
const updateBulkTickets = async (ticketSelections) => {
  isUpdating.value = true
  errorMessage.value = ''
  const { data, statusCode, error } = await patchJson(
    route('api.checkout.order.tickets.update', {
      event: props.eventId,
      order: orderId.value,
    }),
    { tickets: ticketSelections },
  )
  if (statusCode.value && statusCode.value === 404) {
    isExpiredModalShown.value = true
    isUpdating.value = false
    return
  }
  if (error.value) {
    errorMessage.value =
      error.value.message || t('landingpage.checkout-page.error.generic')
    isUpdating.value = false
    return
  }
  // Update order details directly from the PATCH response instead of re-fetching
  if (data.value?.data) {
    orderDetails.value = { ...orderDetails.value, ...data.value.data }
  }
  if (
    lineItemsEmptyOnFirstLoad.value &&
    orderDetails.value?.line_items?.length &&
    orderSummaryRef.value
  ) {
    orderSummaryRef.value.startCountdown()
  }
  isUpdating.value = false
  // Re-fetch available tickets for updated remaining counts (non-blocking)
  fetchOrderTickets()
}

// Assign ticket to Main Guest
const assignTicketToGuest = async (ticket_id) => {
  isLoading.value = true
  const { data, error, statusCode } = await putJson(
    route('api.checkout.guest.ticket.update', {
      event: props.eventId,
      order: orderId.value,
      guest: mainGuest.value?.id,
    }),
    { ticket_id },
  )
  if (statusCode.value && statusCode.value === 404) {
    isExpiredModalShown.value = true
    return
  }

  if (error.value) {
    errorMessage.value =
      data.value?.message || t('landingpage.checkout-page.error.generic')
    isLoading.value = false
    return
  }

  await fetchOrderData(orderId.value)
}

// Fetch guest add-ons
const fetchGuestAddons = async (guest) => {
  isFetchingAddons.value = true
  const { data, statusCode } = await getJson(
    route('api.checkout.guest.addons.index', {
      event: props.eventId,
      order: orderId.value,
      guest: guest.id,
    }),
  )
  if (statusCode.value && statusCode.value === 404) {
    isExpiredModalShown.value = true
    return
  }

  addOns.value = data.value.data
  addOns.value.forEach((item) => {
    item.addons.forEach((addon) => {
      addon.items.map((lineItem) => {
        lineItem.details = orderDetails.value?.line_items.find(
          ({ option_from, option_to }) => {
            return (
              lineItem.starts_at === option_from &&
              lineItem.ends_at === option_to
            )
          },
        )
      })

      addOnsHasPrice.value = addon.options.some(
        (option) => (option.price?.gross || 0) > 0,
      )
    })
  })
  isFetchingAddons.value = false
}

// Assign add-on to Guest
const assignAddOnToGuest = async (addOn, { skipRefetch = false } = {}) => {
  isLoading.value = true
  const { data, error, statusCode } = await postJson(
    route('api.checkout.guest.addons.store', {
      event: props.eventId,
      order: orderId.value,
      guest: mainGuest.value?.id,
    }),
    {
      addon_id: addOn.id,
      start_at: addOn.start_at,
      end_at: addOn.end_at,
      quantity: addOn.quantity,
    },
  )
  if (statusCode.value && statusCode.value === 404) {
    isExpiredModalShown.value = true
    return false
  }

  if (error.value) {
    errorMessage.value =
      data.value?.message || t('landingpage.checkout-page.error.generic')
    isLoading.value = false
    return false
  }

  if (!skipRefetch) {
    await fetchOrderData(orderId.value)
  }
  return true
}

// Remove add-on from Guest
const removeAddOnFromGuest = async (addOn) => {
  isLoading.value = true
  const { statusCode } = await useFetch(
    route('api.checkout.guest.addons.destroy', {
      event: props.eventId,
      order: orderId.value,
      guest: mainGuest.value?.id,
    }),
  ).delete({
    reservation_id: addOn.reservation_id,
  })
  if (statusCode.value && statusCode.value === 404) {
    isExpiredModalShown.value = true
    return
  }

  await fetchOrderData(orderId.value)
}

// Fetch Form definitions
const fetchFormDefinition = async () => {
  isFetchingForm.value = true
  const { data, error, statusCode } = await getJson(
    route('api.checkout.forms.index', {
      event: props.eventId,
      order: orderId.value,
    }),
  )
  if (statusCode.value && statusCode.value === 404) {
    isExpiredModalShown.value = true
    return
  }
  if (error.value) return
  mainGuestFields.value = data.value[props.activeLocale].main
  companionFields.value = data.value[props.activeLocale].companions
  isFetchingForm.value = false
}

// Fetch field option availabilities
const fetchFieldAvailabilities = async () => {
  const { data, statusCode } = await getJson(
    route('api.checkout.field-availabilities.index', {
      event: props.eventId,
      order: orderId.value,
    }),
  )
  if (statusCode.value && statusCode.value === 404) {
    return
  }
  if (data.value?.data) {
    fieldLimitsStore.initialize(data.value.data)
  }
}

// Registry for active companion form data (unsaved edits)
const activeCompanionForms = reactive(new Map())

const registerCompanionForm = (companionId, formDataRef) => {
  activeCompanionForms.set(companionId, formDataRef)
}
const unregisterCompanionForm = (companionId) => {
  activeCompanionForms.delete(companionId)
}

const recalculateFieldLimits = () => {
  const allGuestFormData = []

  // Main guest
  if (mainGuestForm.value.data && mainGuestFields.value) {
    allGuestFormData.push({
      formData: mainGuestForm.value.data,
      fields: mainGuestFields.value,
    })
  }

  // Persisted companions from order (exclude those with active edit forms)
  if (companionGuests.value?.length && companionFields.value) {
    for (const companion of companionGuests.value) {
      if (!activeCompanionForms.has(companion.id)) {
        allGuestFormData.push({
          formData: companion,
          fields: companionFields.value,
        })
      }
    }
  }

  // Active companion forms (unsaved edits)
  for (const [, formDataRef] of activeCompanionForms) {
    if (formDataRef.value && companionFields.value) {
      allGuestFormData.push({
        formData: formDataRef.value,
        fields: companionFields.value,
      })
    }
  }

  fieldLimitsStore.recalculateLocalUsage(allGuestFormData, getFieldPath)
}

// Ticket Guest Registration: fetch form definitions
const fetchTicketGuestRegistrationForms = async () => {
  isFetchingForm.value = true
  const { data, error } = await getJson(
    route('api.checkout.ticket-guest-registration.forms', {
      event: props.eventId,
      guest: props.guestCode,
    }),
  )
  if (error.value) return
  mainGuestFields.value = data.value[props.activeLocale]?.main ?? {}
  isFetchingForm.value = false
}

// Ticket Guest Registration: fetch existing guest data
const fetchTicketGuestRegistrationData = async () => {
  const { data, error } = await getJson(
    route('api.checkout.ticket-guest-registration.show', {
      event: props.eventId,
      guest: props.guestCode,
    }),
  )
  if (error.value) return
  const guestData = data.value.data

  // Build a minimal orderDetails so the template conditions work
  orderDetails.value = {
    booking: {
      guests: [guestData],
    },
  }

  mainGuestForm.value.data = generateGuestFields(
    guestData,
    mainGuestFields.value.items,
  )
}

// Ticket Guest Registration: submit personal data and change status to confirmed
const submitTicketGuestRegistration = async () => {
  if (!userDataRef.value || !userDataRef.value.validate()) {
    return false
  }

  isUpdating.value = true
  const payload = userDataRef.value.getPayload()
  const { data, error } = await useFetch(
    route('api.checkout.ticket-guest-registration.update', {
      event: props.eventId,
      guest: props.guestCode,
      locale: props.activeLocale,
    }),
  ).patch(payload)

  if (error.value) {
    const errorResponse = JSON.parse(data.value)
    if (errorResponse.errors) {
      userDataRef.value.setApiErrors(errorResponse.errors)
    }
    errorMessage.value = errorResponse.message || 'Failed to save guest data'
    isUpdating.value = false
    return false
  }

  isUpdating.value = false
  return true
}

const updateMainGuest = async (guest) => {
  // check validation
  if (!userDataRef.value || !userDataRef.value.validate()) {
    return false
  }

  isUpdating.value = true

  const payload = userDataRef.value.getPayload()
  const { data, error, statusCode } = await useFetch(
    route('api.checkout.guest.update', {
      event: props.eventId,
      order: orderId.value,
      guest: guest.id,
      locale: props.activeLocale,
    }),
  ).patch(payload)

  if (statusCode.value && statusCode.value === 404) {
    isExpiredModalShown.value = true
    return
  }

  if (error.value) {
    const errorResponse = JSON.parse(data.value)
    if (errorResponse.errors) {
      userDataRef.value.setApiErrors(errorResponse.errors)
    }

    errorMessage.value = errorResponse.message || 'Failed to save guest data'
    isUpdating.value = false
    return false
  }

  isUpdating.value = false
  fetchOrderData(orderId.value)
  return true
}

// Update Billing details
// Validate + persist the invoice/billing details to the order. Returns true once saved.
// Shared by the card flow (updateBilling) and the in-app bank-transfer flow (payByBankTransfer)
// so both write the billing address before a payment/invoice is created.
const persistBillingDetails = async () => {
  mainGuestForm.value.billing_errors = {}

  const checkEmpty = requiredBillingFields.filter((key) => {
    const val = mainGuestForm.value.billing_details[key]
    return val === undefined || val === null || val === ''
  })

  if (checkEmpty.length) {
    mainGuestForm.value.billing_errors = Object.fromEntries(
      checkEmpty.map((key) => [key, t('common.this_field_is_required')]),
    )
    return false
  }

  isUpdating.value = true
  let { data, error, statusCode } = await useFetch(
    route('api.checkout.order.update', {
      event: props.eventId,
      order: orderId.value,
    }),
  ).patch(mainGuestForm.value.billing_details)

  if (statusCode.value && statusCode.value === 404) {
    isExpiredModalShown.value = true
    return false
  }

  if (error.value) {
    mainGuestForm.value.billing_errors = data.value.errors
    isUpdating.value = false
    return false
  }

  isUpdating.value = false
  return true
}

const updateBilling = async () => {
  if (await persistBillingDetails()) {
    getCheckoutUrl()
  }
}

/*
 * The payment-url endpoint answers 400 with a stable error_code. Map it to a message the guest can
 * act on - anything unrecognised falls back to the generic payment error rather than a wrong one.
 */
const checkoutUrlErrorMessage = (errorCode) => {
  if (errorCode === 'below_provider_minimum') {
    return t('landingpage.checkout-page.summary.below-minimum-error')
  }

  if (errorCode === 'order_status_final') {
    return t('landingpage.checkout-page.summary.order-already-paid-error')
  }

  if (errorCode === 'unpaid_reservations') {
    return t('landingpage.checkout-page.summary.unpaid-reservations-error')
  }

  return t('landingpage.checkout-page.summary.payment-url-error')
}

// Fetch Payment URL
const getCheckoutUrl = async () => {
  const redirectUrl = new URL(window.location.href)
  redirectUrl.searchParams.set('result', 'success')
  redirectUrl.searchParams.set('order_id', orderId.value)
  isLoading.value = true
  let { data, error, statusCode } = await getJson(
    route('api.checkout.order.payment-url', {
      event: props.eventId,
      order: orderId.value,
      redirect_url: redirectUrl.toString(),
    }),
  )
  if (statusCode.value && statusCode.value === 404) {
    isExpiredModalShown.value = true
    return
  }

  if (error.value) {
    errorMessage.value = checkoutUrlErrorMessage(data.value?.error_code)
    isLoading.value = false
    return
  }
  paymentUrl.value = data.value.data.payment_url
  redirectToPayment()
}

const redirectToPayment = () => {
  if (paymentUrl.value) {
    window.location = paymentUrl.value
  } else {
    errorMessage.value = t(
      'landingpage.checkout-page.summary.payment-url-error',
    )
  }
}

// Bank transfer ("pay by invoice"): create the customer_balance PaymentIntent in-app (no redirect
// to Stripe) and land on our own confirmation page, which shows the IBAN instructions. We reload
// onto the same success URL the card flow returns to, so the existing confirmation path handles it.
const payByBankTransfer = async () => {
  errorMessage.value = ''

  // Save the invoice/billing details first so the bank-transfer order + invoice carry them.
  if (!(await persistBillingDetails())) {
    return
  }

  isLoading.value = true

  const { error, statusCode } = await postJson(
    route('api.checkout.order.bank-transfer', {
      event: props.eventId,
      order: orderId.value,
    }),
  )

  if (statusCode.value && statusCode.value === 404) {
    isExpiredModalShown.value = true
    return
  }

  if (error.value) {
    errorMessage.value = t(
      'landingpage.checkout-page.summary.bank-transfer-error',
    )
    isLoading.value = false
    return
  }

  const redirectUrl = new URL(window.location.href)
  redirectUrl.searchParams.set('result', 'success')
  redirectUrl.searchParams.set('order_id', orderId.value)
  window.location = redirectUrl.toString()
}

const reloadPageWithoutQueryParameters = () => {
  const url = new URL(window.location)
  url.search = ''
  url.hash = ''
  window.location = url.toString()
}

watch(
  () => mainGuestForm.value.data,
  () => recalculateFieldLimits(),
  { deep: true },
)

provide('activeLocale', props.activeLocale)
provide('eventId', props.eventId)
provide('orderId', orderId)
provide('orderDetails', orderDetails)
provide('mainGuest', mainGuest)
provide('mainGuestFields', mainGuestFields)
provide('companionGuests', companionGuests)
provide('companionFields', companionFields)
provide('tickets', tickets)
provide('addOns', addOns)
provide('isFree', isFree)
provide('orderType', orderType)
provide('checkoutSteps', checkoutSteps)
provide('activeStep', activeStep)
provide('activeStepIndex', activeStepIndex)
provide('selectedPaymentMethod', selectedPaymentMethod)
provide('isCompanionsEnabled', isCompanionsEnabled)
provide('eventSettings', props.eventSettings)
provide('countries', props.countries)
provide('checkoutFieldLimitsStore', fieldLimitsStore)
provide('recalculateFieldLimits', recalculateFieldLimits)
provide('registerCompanionForm', registerCompanionForm)
provide('unregisterCompanionForm', unregisterCompanionForm)
provide('generateGuestFields', generateGuestFields)
provide('hasIncompleteCompanions', hasIncompleteCompanions)
provide('sectionThemeClass', sectionThemeClass)
</script>
